// src/features/streams/components/StreamDetailPage.tsx

import { useMemo } from 'react';
import { useParams } from '@tanstack/react-router';
import { Stack, Grid, Loader, Alert, Text, Tabs } from '@mantine/core';
import { IconAlertCircle, IconFileDescription, IconPhoto, IconBroadcast } from '@tabler/icons-react';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useStream } from '../useStream';
import { useStreamLiveStatus } from '../useStreamLiveStatus';
import { StreamHeader } from './StreamHeader';
import { StreamPreview } from './StreamPreview';
import { StreamMetadataCard } from './StreamMetadataCard';
import { StreamOverviewTab } from './StreamOverviewTab';
import { StreamImagesTab } from './StreamImagesTab';
import { StreamSimulcastTab } from './StreamSimulcastTab';
import type { StreamLiveStatus } from '@/lib/types/Stream';
import type { IBackendLiveData } from '@/lib/types/Customer';

export function StreamDetailPage() {
  const { id: displayId } = useParams({ strict: false }) as { id: string };
  const mediaId = bcLong.getNum(displayId);

  const { customerInfo } = useAuth();
  const { data: stream, isLoading, isError, error } = useStream(mediaId);
  const { liveStatusMap } = useStreamLiveStatus();

  // Build backend map from customerInfo for O(1) lookup
  const backendMap = useMemo(() => {
    const map = new Map<number, IBackendLiveData>();
    if (customerInfo?.liveBackends) {
      for (const backend of customerInfo.liveBackends) {
        map.set(backend.backendId, backend);
      }
    }
    return map;
  }, [customerInfo?.liveBackends]);

  // Determine live status for this stream
  const liveStatus: StreamLiveStatus = useMemo(() => {
    const realtimeData = liveStatusMap.get(mediaId);
    if (!realtimeData) return 'offline';
    if (realtimeData.isRecording) return 'recording';
    return 'live';
  }, [liveStatusMap, mediaId]);

  const isLive = liveStatus === 'live' || liveStatus === 'recording';

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading stream...</Text>
      </Stack>
    );
  }

  if (isError || !stream) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error?.message ?? 'Failed to load stream'}
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      <StreamHeader
        stream={stream}
        displayId={displayId}
        liveStatus={liveStatus}
        backendMap={backendMap}
      />

      <Grid>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <StreamPreview stream={stream} isLive={isLive} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <StreamMetadataCard
            stream={stream}
            liveStatus={liveStatus}
            backendMap={backendMap}
          />
        </Grid.Col>
      </Grid>

      <Tabs defaultValue="overview" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconFileDescription size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="images" leftSection={<IconPhoto size={16} />}>
            Images
          </Tabs.Tab>
          <Tabs.Tab value="simulcast" leftSection={<IconBroadcast size={16} />}>
            Destinations
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md" keepMounted>
          <StreamOverviewTab stream={stream} />
        </Tabs.Panel>

        <Tabs.Panel value="images" pt="md" keepMounted>
          <StreamImagesTab stream={stream} />
        </Tabs.Panel>

        <Tabs.Panel value="simulcast" pt="md">
          <StreamSimulcastTab stream={stream} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
